import React from 'react';
import {
  Paper,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  alpha,
  Tooltip,
  Divider,
  Button,
  Alert,
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  FormatListNumbered as FormatListNumberedIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  HourglassEmpty as HourglassEmptyIcon,
  ChangeCircle as ChangeIcon,
  Accessible as AccessibleIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { pt } from 'date-fns/locale';

const FilaPasswordsList = ({
  proximasSenhas,
  handleClicarSenha,
  handleChamarProxima,
  carregando,
  customTheme,
  chamadaEmAndamento,
  aguardandoChamada,
  senhaChamada,
  atendimentoFinalizado,
}) => {
  // Formatar hora para exibição
  const formatarHora = (data) => {
    if (!data) return '';
    
    try {
      const dataObj = new Date(data);
      return format(dataObj, 'HH:mm', { locale: pt });
    } catch (error) {
      console.error('Erro ao formatar data:', error);
      return '';
    }
  };

  // Verificar se há uma senha ativa (chamada mas não finalizada)
  const temSenhaAtiva = senhaChamada && !atendimentoFinalizado;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        height: '100%',
        maxHeight: { xs: '400px', md: '600px' },
        display: 'flex',
        flexDirection: 'column',
        border: theme => `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid',
          borderColor: alpha(customTheme.palette.primary.main, 0.1),
          bgcolor: alpha(customTheme.palette.primary.main, 0.05),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <FormatListNumberedIcon sx={{ color: 'primary.main', mr: 1.5 }} />
          <Typography variant="subtitle1" fontWeight={600}>
            Fila de Espera
          </Typography>
        </Box>
        <Chip
          label={`${proximasSenhas.length} ${proximasSenhas.length === 1 ? 'senha' : 'senhas'}`}
          size="small"
          color="primary"
          variant="outlined"
          sx={{ fontWeight: 500 }}
        />
      </Box>

      {/* Alerta de chamada em andamento */}
      {aguardandoChamada && (
        <Alert 
          severity="info" 
          icon={<HourglassEmptyIcon fontSize="inherit" />}
          sx={{ 
            m: 2, 
            borderRadius: 2,
            boxShadow: theme => `0 2px 8px ${alpha(customTheme.palette.primary.main, 0.2)}`,
          }}
        >
          Aguardando chamada da senha anterior para chamar a sua
        </Alert>
      )}

      <Box sx={{ overflow: 'auto', flexGrow: 1, maxHeight: 'calc(100% - 56px - 68px)', overflowX: 'hidden' }}>
        {proximasSenhas.length === 0 && !carregando ? (
          <Box
            sx={{
              p: 3,
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%'
            }}
          >
            <AssignmentTurnedInIcon
              sx={{
                fontSize: 40,
                mb: 2,
                color: alpha(customTheme.palette.success.main, 0.7)
              }}
            />
            <Typography variant="h6" sx={{ mb: 1 }}>
              Fila Vazia
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Não há senhas aguardando atendimento.
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {proximasSenhas.map((senha, index) => (
              <React.Fragment key={senha.id || index}>
                {index > 0 && <Divider component="li" />}
                <ListItem
                  button
                  onClick={() => handleClicarSenha(senha)}
                  sx={{
                    py: 1.5,
                    px: 2,
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: alpha(customTheme.palette.primary.main, 0.05),
                      scale: 1.02,
                    },
                    cursor: 'pointer',
                  }}
                >
                  <Box
                    sx={{
                      mr: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      bgcolor: senha.preferencial
                        ? alpha(customTheme.palette.secondary.main, 0.1)
                        : alpha(customTheme.palette.primary.main, 0.1),
                      color: senha.preferencial
                        ? customTheme.palette.secondary.main
                        : customTheme.palette.primary.main
                    }}
                  >
                    {senha.preferencial ? (
                      <AccessibleIcon fontSize="small" />
                    ) : (
                      <HourglassEmptyIcon fontSize="small" />
                    )}
                  </Box>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography
                          variant="subtitle2"
                          component="span"
                          sx={{
                            fontWeight: 600
                          }}
                        >
                          {senha.numero}
                        </Typography>
                        <Typography
                          variant="caption"
                          component="span"
                          sx={{
                            ml: 1,
                            color: 'text.secondary',
                            fontWeight: 500
                          }}
                        >
                          • {senha.tipo}
                        </Typography>
                        <Typography
                          variant="caption"
                          component="span"
                          sx={{
                            ml: 1,
                            color: 'text.secondary'
                          }}
                        >
                          {senha.emitida_em && `Emitida: ${formatarHora(senha.emitida_em)}`}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                        <Chip
                          label="Aguardando"
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            '& .MuiChip-label': {
                              px: 0.8
                            }
                          }}
                        />
                        {senha.preferencial && (
                          <Chip
                            label="Preferencial"
                            size="small"
                            color="secondary"
                            variant="outlined"
                            sx={{
                              ml: 1,
                              height: 20,
                              fontSize: '0.65rem',
                              '& .MuiChip-label': {
                                px: 0.8
                              }
                            }}
                          />
                        )}
                        {senha.servico_alterado && senha.servico_original && (
                          <Tooltip title={`Esta senha foi alterada do serviço original: ${senha.servico_original}`}>
                            <Chip
                              icon={<ChangeIcon fontSize="small" />}
                              label={`Serviço Alterado`}
                              size="small"
                              variant="outlined"
                              sx={{
                                ml: 1,
                                height: 20,
                                fontSize: '0.65rem',
                                borderColor: customTheme.palette.warning.main,
                                color: customTheme.palette.warning.main,
                                backgroundColor: alpha(customTheme.palette.warning.main, 0.1),
                                cursor: 'help',
                                '& .MuiChip-label': {
                                  px: 0.8
                                },
                                '& .MuiChip-icon': {
                                  color: customTheme.palette.warning.main,
                                  fontSize: '0.75rem',
                                }
                              }}
                            />
                          </Tooltip>
                        )}
                        {senha.servicos_adicionais && senha.servicos_adicionais.length > 0 && (
                          <Tooltip 
                            title={
                              <Box>
                                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                                  Serviços Adicionais:
                                </Typography>
                                {senha.servicos_adicionais.map((servico, index) => (
                                  <Typography key={index} variant="caption" sx={{ display: 'block' }}>
                                    • {servico}
                                  </Typography>
                                ))}
                              </Box>
                            }
                            arrow
                          >
                            <Chip
                              label={`+${senha.servicos_adicionais.length} serviço${senha.servicos_adicionais.length > 1 ? 's' : ''}`}
                              size="small"
                              color="secondary"
                              variant="outlined"
                              sx={{
                                ml: 1,
                                height: 20,
                                fontSize: '0.65rem',
                                cursor: 'help',
                                '& .MuiChip-label': {
                                  px: 0.8
                                }
                              }}
                            />
                          </Tooltip>
                        )}
                        {senha.tempEspera && (
                          <Typography
                            variant="caption"
                            sx={{
                              ml: 'auto',
                              color: 'text.secondary'
                            }}
                          >
                            {senha.tempEspera}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </Box>

      <Box sx={{ p: 2, borderTop: `1px solid ${alpha(customTheme.palette.divider, 0.1)}` }}>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          disabled={proximasSenhas.length === 0 || carregando || aguardandoChamada || temSenhaAtiva}
          startIcon={<AssignmentTurnedInIcon />}
          onClick={handleChamarProxima}
          sx={{
            py: 1.5,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            boxShadow: theme => proximasSenhas.length === 0 ? 'none' : `0 4px 14px ${alpha(customTheme.palette.primary.main, 0.4)}`,
          }}
        >
          {carregando ? 'Carregando...' : 
           aguardandoChamada ? 'Aguardando chamada anterior...' : 
           temSenhaAtiva ? 'Finalize o atendimento atual' :
           proximasSenhas.length === 0 ? 'Fila Vazia' : 
           'Chamar Próxima Senha'}
        </Button>
      </Box>
    </Paper>
  );
};

export default FilaPasswordsList; 